import { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import getConfig from 'next/config';
import { useAuthentication } from '@context/businessContainer';
import { CREATE_YUMA_CHAT_TOKEN } from '@monofront/business/queries';
import { useMediaQuery } from '@monofront/business/hooks';
import { useGlobalSearchContext } from '@context/GlobalSearchContext';
import logger from '@utils/logger';

interface CreateYumaChatTokenResponse {
  createYumaChatToken: string;
}

declare global {
  interface Window {
    $yuma: any;
  }
}

const {
  publicRuntimeConfig: { yumaWidgetUrl },
} = getConfig();

const YUMA_SCRIPT_ID = 'yuma-widget';
const YUMA_LOADER_SCRIPT_URL = 'https://js.yuma.ai/widget.js';
// Yuma injects its iframe into this container (via config.target) instead of
// `body`, so cleanup/styling can target a selector we own rather than
// matching on Yuma's iframe src, which could change without notice.
const YUMA_CONTAINER_ID = 'yuma-widget-container';
const YUMA_BUTTON_ID = 'yuma-custom-btn';

const teardownYuma = () => {
  try {
    document.getElementById(YUMA_SCRIPT_ID)?.remove();
    document.getElementById(YUMA_CONTAINER_ID)?.replaceChildren();
  } catch (error) {
    logger.error('Yuma teardown failed', { error });
  } finally {
    window.$yuma = undefined;
  }
};

const injectYumaScript = (properties: Record<string, string>) => {
  window.$yuma = [];
  window.$yuma.url = yumaWidgetUrl;
  window.$yuma.config = { properties, target: `#${YUMA_CONTAINER_ID}` };

  const script = document.createElement('script');

  script.id = YUMA_SCRIPT_ID;
  script.async = true;
  script.src = YUMA_LOADER_SCRIPT_URL;
  document.head.appendChild(script);
};

const YumaWidget = () => {
  const { customer } = useAuthentication();
  const [createYumaChatToken] = useMutation<CreateYumaChatTokenResponse>(
    CREATE_YUMA_CHAT_TOKEN,
  );
  const isMobileWebsite = useMediaQuery(800);
  const { isGlobalSearchResultsPanelOpen } = useGlobalSearchContext();
  const isMobileSearchPanelOpen =
    isMobileWebsite && isGlobalSearchResultsPanelOpen;
  const [isChatOpen, setIsChatOpen] = useState(false);
  // The launcher must disappear both while the chat itself is open (it would
  // sit on top of the conversation) and while the mobile search panel is
  // open (same reason the widget iframe is told to `hide` below) — two
  // distinct reasons, so this isn't named after either one specifically.
  const isLauncherHidden = isMobileSearchPanelOpen || isChatOpen;

  useEffect(() => {
    const handleWidgetMessage = (event: MessageEvent) => {
      if (!event.data || typeof event.data !== 'object') return;
      if (event.data.action === 'setWidgetState') {
        setIsChatOpen(event.data.data?.state !== 'closed');
      }
    };

    window.addEventListener('message', handleWidgetMessage);

    return () => window.removeEventListener('message', handleWidgetMessage);
  }, []);

  const handleToggleChat = () => {
    if (!window.$yuma?.push) return;
    window.$yuma.push([isChatOpen ? 'close' : 'open']);
    setIsChatOpen((previous) => !previous);
  };

  useEffect(() => {
    const properties: Record<string, string> = {};

    if (customer) {
      if (customer.email) properties.customerEmail = customer.email;
      if (customer.firstName) properties.firstName = customer.firstName;
      if (customer.lastName) properties.lastName = customer.lastName;

      customer.omsTags?.forEach((omsTag) => {
        properties[omsTag] = 'true';
      });
    }

    injectYumaScript(properties);

    if (customer?.uid) {
      // Our Yuma JWT is short-lived (60s, see lego's yumaChatToken service),
      // so a token minted once at load expires mid-conversation and Yuma
      // silently treats the customer as signed out. Passing a provider lets
      // the widget re-mint on demand right before it needs a fresh one.
      window.$yuma?.push?.([
        'identify',
        {
          token: async () => {
            try {
              const { data } = await createYumaChatToken();

              return data?.createYumaChatToken;
            } catch (error) {
              logger.error('Failed to mint Yuma identify token', { error });

              return null;
            }
          },
        },
      ]);
    }

    return () => teardownYuma();
  }, [customer?.uid, createYumaChatToken]);

  useEffect(() => {
    window.$yuma?.push?.([isMobileSearchPanelOpen ? 'hide' : 'show']);
  }, [customer?.uid, isMobileSearchPanelOpen]);

  return (
    <>
      <div id={YUMA_CONTAINER_ID} />
      <button
        id={YUMA_BUTTON_ID}
        type="button"
        aria-label="Ouvrir le chat"
        className={isLauncherHidden ? 'hidden' : ''}
        onClick={handleToggleChat}
      >
        <svg
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <rect
            x="7"
            y="9"
            width="26"
            height="18"
            rx="9"
            stroke="#23241D"
            strokeWidth="2"
          />
          <circle cx="15" cy="18" r="1.6" fill="#23241D" />
          <circle cx="20" cy="18" r="1.6" fill="#23241D" />
          <circle cx="25" cy="18" r="1.6" fill="#23241D" />
        </svg>
      </button>
      <style jsx global>{`
        @media (max-width: 600px) {
          :global(#yuma-widget-container iframe) {
            margin-bottom: 10px;
          }
        }

        @media (min-width: 600px) and (max-width: 800px) {
          :global(#yuma-widget-container iframe) {
            margin-bottom: 50px;
          }
        }

        /* The default Yuma launcher renders as a small (62/72px) iframe;
           it stays large when the chat is open, so this only hides the
           closed-state button, replaced below by our own custom button. */
        :global(#yuma-widget-container iframe[style*='62px']),
        :global(#yuma-widget-container iframe[style*='72px']) {
          display: none !important;
        }

        :global(#yuma-custom-btn) {
          position: fixed;
          bottom: 24px;
          right: 24px;
          z-index: 9998;
          width: 60px;
          height: 60px;
          border-radius: 24px 24px 24px 0;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--Base-yellow600, #ffcb03);
          box-shadow: 0 4px 10px 0 rgba(0, 0, 0, 0.14);
          transition: transform 0.18s ease, box-shadow 0.18s ease,
            opacity 0.18s ease;
        }

        :global(#yuma-custom-btn:hover) {
          transform: scale(1.06);
          box-shadow: 0 6px 16px 0 rgba(0, 0, 0, 0.2);
        }

        :global(#yuma-custom-btn.hidden) {
          visibility: hidden;
          opacity: 0;
          pointer-events: none;
        }
      `}</style>
    </>
  );
};

export default YumaWidget;
